import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { api } from '../services/api';
import { useCondominio } from '../contexts/CondominioContext';
import { useAuth } from '../contexts/AuthContext';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Eye,
  Filter,
  MapPin,
  Calendar,
  User
} from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';

const AchadosPerdidos = () => {
  const { getCondominioId, isAdministrador } = useCondominio();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    tipo: '',
    categoria: '',
    status: ''
  });

  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, pages: 1 });

  // Buscar achados e perdidos (primeira tela: 10 registros)
  const { data: achadosPerdidosData, isLoading } = useQuery(
    ['achados-perdidos', getCondominioId(), searchTerm, filters, pagination.page],
    () => api.get('/achados-perdidos', {
      params: {
        search: searchTerm,
        page: pagination.page,
        limit: pagination.limit,
        ...filters
      }
    }).then(res => res.data.data),
    {
      enabled: !!getCondominioId()
    }
  );

  React.useEffect(() => {
    const pag = achadosPerdidosData?.pagination;
    if (pag && (pag.total !== undefined || pag.pages !== undefined)) {
      setPagination(prev => ({ ...prev, total: pag.total ?? prev.total, pages: Math.max(1, pag.pages ?? prev.pages) }));
    }
  }, [achadosPerdidosData?.pagination?.total, achadosPerdidosData?.pagination?.pages]);

  // Buscar apartamentos para o formulário
  const { data: apartamentosData } = useQuery(
    ['apartamentos', getCondominioId()],
    () => api.get('/apartamentos').then(res => res.data.data.apartamentos),
    {
      enabled: !!getCondominioId()
    }
  );

  // Buscar moradores para o formulário
  const { data: moradoresData } = useQuery(
    ['moradores', getCondominioId()],
    () => api.get('/moradores').then(res => res.data.data.moradores),
    {
      enabled: !!getCondominioId()
    }
  );

  // Criar/atualizar achado/perdido
  const itemMutation = useMutation(
    (itemData) => {
      if (editingItem) {
        return api.put(`/achados-perdidos/${editingItem.achado_perdido_id}`, itemData);
      } else {
        return api.post('/achados-perdidos', itemData);
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['achados-perdidos']);
        setShowModal(false);
        setEditingItem(null);
      }
    }
  );

  // Excluir achado/perdido
  const deleteMutation = useMutation(
    (id) => api.delete(`/achados-perdidos/${id}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['achados-perdidos']);
      }
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const itemData = Object.fromEntries(formData.entries());
    
    itemMutation.mutate(itemData);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setShowModal(true);
  };

  const handleDelete = (id) => {
    if (window.confirm('Tem certeza que deseja excluir este item?')) {
      deleteMutation.mutate(id);
    }
  };

  const resetForm = () => {
    setEditingItem(null);
    setShowModal(false);
  };

  const getTipoIcon = (tipo) => {
    return tipo === 'achado' ? '🔍' : '❌';
  };

  const getTipoColor = (tipo) => {
    return tipo === 'achado' ? 'text-green-600' : 'text-red-600';
  };

  const getCategoriaIcon = (categoria) => {
    const icons = {
      documentos: '📄',
      chaves: '🔑',
      eletronicos: '📱',
      roupas: '👕',
      acessorios: '👜',
      outros: '📦'
    };
    return icons[categoria] || '📦';
  };

  const getStatusColor = (status) => {
    const colors = {
      ativo: 'bg-blue-100 text-blue-800',
      resolvido: 'bg-green-100 text-green-800',
      arquivado: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (isLoading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Achados e Perdidos</h1>
          <p className="text-gray-600">Gerencie itens achados e perdidos no condomínio</p>
        </div>
        {isAdministrador() && (
          <button
            onClick={() => setShowModal(true)}
            className="btn-primary"
          >
            <Plus className="h-4 w-4 mr-2" />
            Cadastrar Item
          </button>
        )}
      </div>

      {/* Filtros */}
      <div className="card">
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Buscar
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Título ou descrição..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="input-field pl-10"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tipo
              </label>
              <select
                value={filters.tipo}
                onChange={(e) => setFilters({...filters, tipo: e.target.value})}
                className="input-field"
              >
                <option value="">Todos</option>
                <option value="achado">Achado</option>
                <option value="perdido">Perdido</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Categoria
              </label>
              <select
                value={filters.categoria}
                onChange={(e) => setFilters({...filters, categoria: e.target.value})}
                className="input-field"
              >
                <option value="">Todas</option>
                <option value="documentos">Documentos</option>
                <option value="chaves">Chaves</option>
                <option value="eletronicos">Eletrônicos</option>
                <option value="roupas">Roupas</option>
                <option value="acessorios">Acessórios</option>
                <option value="outros">Outros</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={filters.status}
                onChange={(e) => setFilters({...filters, status: e.target.value})}
                className="input-field"
              >
                <option value="">Todos</option>
                <option value="ativo">Ativo</option>
                <option value="resolvido">Resolvido</option>
                <option value="arquivado">Arquivado</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Lista de Itens */}
      <div className="card">
        <div className="card-body">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Item
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tipo
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Categoria
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Local
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Data
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {achadosPerdidosData?.achadosPerdidos?.map((item) => (
                  <tr key={item.achado_perdido_id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="text-2xl mr-3">
                          {getCategoriaIcon(item.achado_perdido_categoria)}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {item.achado_perdido_titulo}
                          </div>
                          <div className="text-sm text-gray-500 max-w-xs truncate">
                            {item.achado_perdido_descricao}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`flex items-center text-sm ${getTipoColor(item.achado_perdido_tipo)}`}>
                        <span className="mr-1">{getTipoIcon(item.achado_perdido_tipo)}</span>
                        <span className="capitalize">{item.achado_perdido_tipo}</span>
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="capitalize text-sm text-gray-900">
                        {item.achado_perdido_categoria}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.achado_perdido_local || 'Não informado'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.achado_perdido_data_ocorrencia ? 
                        new Date(item.achado_perdido_data_ocorrencia).toLocaleDateString('pt-BR') : 
                        'Não informado'
                      }
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(item.achado_perdido_status)}`}>
                        {item.achado_perdido_status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        {isAdministrador() && (
                          <>
                            <button
                              onClick={() => handleEdit(item)}
                              className="text-blue-600 hover:text-blue-900"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(item.achado_perdido_id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal de Cadastro/Edição */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {editingItem ? 'Editar Item' : 'Cadastrar Item'}
                </h3>
                <button
                  onClick={resetForm}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Título *
                    </label>
                    <input
                      type="text"
                      name="achado_perdido_titulo"
                      defaultValue={editingItem?.achado_perdido_titulo || ''}
                      required
                      className="input-field"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tipo *
                    </label>
                    <select
                      name="achado_perdido_tipo"
                      defaultValue={editingItem?.achado_perdido_tipo || ''}
                      required
                      className="input-field"
                    >
                      <option value="">Selecione</option>
                      <option value="achado">Achado</option>
                      <option value="perdido">Perdido</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Categoria *
                    </label>
                    <select
                      name="achado_perdido_categoria"
                      defaultValue={editingItem?.achado_perdido_categoria || ''}
                      required
                      className="input-field"
                    >
                      <option value="">Selecione</option>
                      <option value="documentos">Documentos</option>
                      <option value="chaves">Chaves</option>
                      <option value="eletronicos">Eletrônicos</option>
                      <option value="roupas">Roupas</option>
                      <option value="acessorios">Acessórios</option>
                      <option value="outros">Outros</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Local
                    </label>
                    <input
                      type="text"
                      name="achado_perdido_local"
                      defaultValue={editingItem?.achado_perdido_local || ''}
                      className="input-field"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Data da Ocorrência
                    </label>
                    <input
                      type="datetime-local"
                      name="achado_perdido_data_ocorrencia"
                      defaultValue={editingItem?.achado_perdido_data_ocorrencia ? 
                        new Date(editingItem.achado_perdido_data_ocorrencia).toISOString().slice(0, 16) : ''}
                      className="input-field"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <select
                      name="achado_perdido_status"
                      defaultValue={editingItem?.achado_perdido_status || 'ativo'}
                      className="input-field"
                    >
                      <option value="ativo">Ativo</option>
                      <option value="resolvido">Resolvido</option>
                      <option value="arquivado">Arquivado</option>
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descrição *
                  </label>
                  <textarea
                    name="achado_perdido_descricao"
                    defaultValue={editingItem?.achado_perdido_descricao || ''}
                    rows={3}
                    required
                    className="input-field"
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Apartamento
                    </label>
                    <select
                      name="apartamento_id"
                      defaultValue={editingItem?.apartamento_id || ''}
                      className="input-field"
                    >
                      <option value="">Selecione</option>
                      {apartamentosData?.map((apartamento) => (
                        <option key={apartamento.apartamento_id} value={apartamento.apartamento_id}>
                          {apartamento.apartamento_bloco} - {apartamento.apartamento_numero}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Morador
                    </label>
                    <select
                      name="morador_id"
                      defaultValue={editingItem?.morador_id || ''}
                      className="input-field"
                    >
                      <option value="">Selecione</option>
                      {moradoresData?.map((morador) => (
                        <option key={morador.morador_id} value={morador.morador_id}>
                          {morador.morador_nome}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nome para Contato
                    </label>
                    <input
                      type="text"
                      name="achado_perdido_contato_nome"
                      defaultValue={editingItem?.achado_perdido_contato_nome || ''}
                      className="input-field"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Telefone para Contato
                    </label>
                    <input
                      type="tel"
                      name="achado_perdido_contato_telefone"
                      defaultValue={editingItem?.achado_perdido_contato_telefone || ''}
                      className="input-field"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email para Contato
                  </label>
                  <input
                    type="email"
                    name="achado_perdido_contato_email"
                    defaultValue={editingItem?.achado_perdido_contato_email || ''}
                    className="input-field"
                  />
                </div>
                
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="btn-secondary"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={itemMutation.isLoading}
                    className="btn-primary"
                  >
                    {itemMutation.isLoading ? 'Salvando...' : 'Salvar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AchadosPerdidos;
